import {computed, effect, Injectable, Injector, OnDestroy, runInInjectionContext, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Subject, Subscription} from 'rxjs';
import {UndoableCommand} from './interfaces/command.interface';
import {MapService} from '../../api/services/map.service';
import {UserService} from '../../api/services/user.service';
import {NotificationService} from '../notification.service';
import {undoRedoJustHappened} from '../../shared/helpers/signal.helpers';
import {translateUndoLabel} from './undo-label.utils';

// Subject for lightweight undo/redo menu updates (no API calls)
export const undoRedoStateChanged$ = new Subject<void>();

const MAX_STACK_SIZE = 50;

@Injectable({
  providedIn: 'root'
})
export class UndoRedoManagerService implements OnDestroy {

  // Stack management using signals for reactivity
  private undoStackSignal = signal<UndoableCommand[]>([]);
  private redoStackSignal = signal<UndoableCommand[]>([]);

  // Computed signals for UI bindings
  public canUndo = computed(() => this.undoStackSignal().length > 0);
  public canRedo = computed(() => this.redoStackSignal().length > 0);

  public undoDescription = computed(() => {
    const stack = this.undoStackSignal();
    return stack.length > 0 ? this.describeCommand(stack[stack.length - 1]) : null;
  });

  public redoDescription = computed(() => {
    const stack = this.redoStackSignal();
    return stack.length > 0 ? this.describeCommand(stack[stack.length - 1]) : null;
  });

  /**
   * Resolve a command's user-facing label. When structured labelMetadata is
   * present the label is composed from i18n keys (identical rendered English);
   * otherwise the command's raw English description is used as a fallback.
   */
  private describeCommand(command: UndoableCommand): string {
    return command.labelMetadata
      ? translateUndoLabel(command.labelMetadata, this.translate)
      : command.description;
  }

  public undoStackSize = computed(() => this.undoStackSignal().length);
  public redoStackSize = computed(() => this.redoStackSignal().length);

  // Event subjects for external listeners
  private commandExecuted$ = new Subject<{ command: UndoableCommand; action: 'do' | 'undo' | 'redo' }>();
  private stackReset$ = new Subject<void>();

  // Track if an operation is in progress to prevent concurrent operations
  private isOperationInProgress = signal(false);

  private subscriptions = new Subscription();
  private lastMapId: string | null = null;
  private lastUserId: string | null = null;
  private initialized = false;

  constructor(
    private mapService: MapService,
    private userService: UserService,
    private injector: Injector,
    private translate: TranslateService,
    private notificationService: NotificationService
  ) {
    // Defer initialization to avoid constructor injection issues
    setTimeout(() => this.initialize(), 0);
  }

  private initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Debug function for console
    (window as any).logUndo = () => console.table(this.undoStackSignal().map(c => ({
      type: c.type,
      desc: c.description,
      data: c
    })));

    runInInjectionContext(this.injector, () => {
      // Watch for map changes using effect
      effect(() => {
        const currentMap = this.mapService.selectedMap();
        const currentMapId = currentMap?.id ?? null;

        if (this.lastMapId !== null && this.lastMapId !== currentMapId) {
          // Map changed, reset stacks
          this.resetStacks();
        }
        this.lastMapId = currentMapId;
      }, {allowSignalWrites: true});

      // Watch for user changes (sign out) using effect
      effect(() => {
        const currentUser = this.userService.user();
        const currentUserId = currentUser?.id ?? null;

        if (this.lastUserId !== null && currentUserId === null) {
          // User signed out, reset stacks
          this.resetStacks();
        }
        this.lastUserId = currentUserId;
      }, {allowSignalWrites: true});
    });

    // Reset on window unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.resetStacks());
    }
  }

  /**
   * Execute a command and add it to the undo stack.
   * The command's execute() method is called, and the command is pushed to the stack.
   */
  async executeCommand(command: UndoableCommand): Promise<void> {
    if (this.isOperationInProgress()) {
      console.warn('UndoRedoManager: Operation already in progress, skipping command');
      return;
    }

    this.isOperationInProgress.set(true);

    try {
      await command.execute();

      const currentStack = this.undoStackSignal();
      const lastCommand = currentStack[currentStack.length - 1];

      // Check if we can merge with the last command (text coalescing)
      if (lastCommand && command.canMerge && command.canMerge(lastCommand)) {
        if (lastCommand.merge) {
          lastCommand.merge(command);
        }
        // Trigger signal update
        this.undoStackSignal.set([...currentStack]);
      } else {
        // Add new command to stack with FIFO purge if needed
        let newStack = [...currentStack, command];
        if (newStack.length > MAX_STACK_SIZE) {
          newStack = newStack.slice(newStack.length - MAX_STACK_SIZE);
        }
        this.undoStackSignal.set(newStack);
      }

      // Clear redo stack on new command
      this.redoStackSignal.set([]);

      this.commandExecuted$.next({command, action: 'do'});
      undoRedoStateChanged$.next();
    } catch (error) {
      console.error('UndoRedoManager: Failed to execute command:', error);
      throw error;
    } finally {
      this.isOperationInProgress.set(false);
    }
  }

  /**
   * Add a command to the undo stack without executing it.
   * Useful when the action has already been performed and you just want to track it.
   */
  pushCommand(command: UndoableCommand): void {
    const currentStack = this.undoStackSignal();
    const lastCommand = currentStack[currentStack.length - 1];

    // Check if we can merge with the last command (text coalescing)
    if (lastCommand && command.canMerge && command.canMerge(lastCommand)) {
      if (lastCommand.merge) {
        lastCommand.merge(command);
      }
      this.undoStackSignal.set([...currentStack]);
    } else {
      let newStack = [...currentStack, command];
      if (newStack.length > MAX_STACK_SIZE) {
        newStack = newStack.slice(newStack.length - MAX_STACK_SIZE);
      }
      this.undoStackSignal.set(newStack);
    }

    // Clear redo stack
    this.redoStackSignal.set([]);

    this.commandExecuted$.next({command, action: 'do'});
    undoRedoStateChanged$.next();
  }

  /**
   * Undo the last command
   */
  async undo(): Promise<boolean> {
    const stack = this.undoStackSignal();
    if (stack.length === 0) {
      return false;
    }

    if (this.isOperationInProgress()) {
      return false;
    }

    this.isOperationInProgress.set(true);
    // Signal that undo/redo is happening - forms should sync with the resulting state
    undoRedoJustHappened.set(true);

    const command = stack[stack.length - 1];

    try {
      await command.undo();

      // Move command to redo stack
      this.undoStackSignal.set(stack.slice(0, -1));
      this.redoStackSignal.update(redo => [...redo, command]);

      this.commandExecuted$.next({command, action: 'undo'});
      undoRedoStateChanged$.next();

      // Reset the signal after a delay longer than the debounce time (500ms) to block any pending emissions
      setTimeout(() => undoRedoJustHappened.set(false), 700);

      return true;
    } catch (error) {
      // MAP-1547: on failure, the command must not stay silently stuck on top
      // of the stack — that made retrying "undo" look like it does nothing,
      // forever, with no feedback. Drop the failed command so the stack can
      // move on, and reset the signal immediately instead of leaving it
      // stuck true (the success path's delayed reset never runs on this path).
      console.error('UndoRedoManager: Failed to undo command:', command.type, error);
      this.undoStackSignal.set(stack.slice(0, -1));
      undoRedoJustHappened.set(false);
      // MAP-1547: none of undo()'s callers (context-menu clicks, Cmd/Ctrl+Z)
      // wrap this in a try/catch, so a thrown error here previously became an
      // unhandled promise rejection — invisible to the user, who just saw
      // "nothing happen". Surface it instead of throwing.
      this.notificationService.error(this.translate.instant('DASHBOARD_MSG.MENU.UNDO_FAILED'));
      return false;
    } finally {
      this.isOperationInProgress.set(false);
    }
  }

  /**
   * Redo the last undone command
   */
  async redo(): Promise<boolean> {
    const stack = this.redoStackSignal();
    if (stack.length === 0) {
      return false;
    }

    if (this.isOperationInProgress()) {
      console.warn('UndoRedoManager: Operation already in progress');
      return false;
    }

    this.isOperationInProgress.set(true);
    // Signal that undo/redo is happening - forms should sync with the resulting state
    undoRedoJustHappened.set(true);

    const command = stack[stack.length - 1];

    try {
      await command.execute();

      // Move command back to undo stack
      this.redoStackSignal.set(stack.slice(0, -1));
      this.undoStackSignal.update(undo => [...undo, command]);

      this.commandExecuted$.next({command, action: 'redo'});
      undoRedoStateChanged$.next();

      // Reset the signal after a delay longer than the debounce time (500ms) to block any pending emissions
      setTimeout(() => undoRedoJustHappened.set(false), 700);

      return true;
    } catch (error) {
      // MAP-1547: same fix as undo() — don't leave a failed command stuck on
      // top of the redo stack with the signal permanently true.
      console.error('UndoRedoManager: Failed to redo command:', command.type, error);
      this.redoStackSignal.set(stack.slice(0, -1));
      undoRedoJustHappened.set(false);
      this.notificationService.error(this.translate.instant('DASHBOARD_MSG.MENU.REDO_FAILED'));
      return false;
    } finally {
      this.isOperationInProgress.set(false);
    }
  }

  /**
   * Clear both stacks
   */
  resetStacks(): void {
    this.undoStackSignal.set([]);
    this.redoStackSignal.set([]);
    this.stackReset$.next();
    undoRedoStateChanged$.next();
  }

  /**
   * Get observable for command execution events
   */
  onCommandExecuted() {
    return this.commandExecuted$.asObservable();
  }

  /**
   * Get observable for stack reset events
   */
  onStackReset() {
    return this.stackReset$.asObservable();
  }

  /**
   * Check if an operation is currently in progress
   */
  isOperating(): boolean {
    return this.isOperationInProgress();
  }

  /**
   * MAP-1547: true when the most recent undo entry is the creation of the given
   * element (node or link). Callers use this to fold an element's immediate
   * post-creation edits (e.g. the style/properties a model applies, or naming a
   * just-created element) INTO the creation entry instead of pushing separate
   * entries — so undoing a model-created element takes a single step.
   */
  lastCommandIsCreationOf(entityId: string): boolean {
    if (!entityId) return false;
    const stack = this.undoStackSignal();
    const last = stack[stack.length - 1];
    if (!last) return false;
    if (last.type !== 'create-node' && last.type !== 'create-link') return false;
    return (last as unknown as { createdEntityId?: string }).createdEntityId === entityId;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
